import { isValid } from "date-fns";
import isUrl from "is-url";

import type {
	Flashcard,
	FlashcardSet,
	FlashcardSetPopulated,
} from "@vita-learning/types";

import { decodeHtml, EnhancedError, isEqual, isHexId } from "~/shared/utils";

import { FLASHCARD_SET_SOURCES } from "./constants";
import type { FlashcardSetInfoForSource } from "./types";

const isFlashcardSetSource = (
	source: unknown
): source is FlashcardSet["source"] =>
	FLASHCARD_SET_SOURCES.some(s => s === source);

export enum FlashcardErrorId {
	USER = "FLASHCARDS_USER",
	TITLE = "FLASHCARDS_TITLE",
	DESCRIPTION = "FLASHCARDS_DESCRIPTION",
	DATE = "FLASHCARDS_DATE",
	SOURCE = "FLASHCARDS_SOURCE",
	CARDS = "FLASHCARDS_CARDS",
	FILE = "FLASHCARDS_FILE",
	NOTE = "FLASHCARDS_NOTE",
	URL = "FLASHCARDS_URL",
}

export function assertsValidPartialFlashcardSet(
	flashcardSet: unknown,
	file?: File
): asserts flashcardSet is FlashcardSetInfoForSource {
	if (!flashcardSet || typeof flashcardSet !== "object")
		throw new Error("Flashcard set must be an object");

	if (!("id" in flashcardSet) || !isHexId(flashcardSet.id))
		throw new Error("Flashcard set requires a valid ID");

	if (!("userId" in flashcardSet) || !isHexId(flashcardSet.userId))
		throw new Error("Flashcard set requires a valid user ID");

	if (
		!("title" in flashcardSet) ||
		!flashcardSet.title ||
		typeof flashcardSet.title !== "string"
	)
		throw new EnhancedError(
			"Title is a required field",
			FlashcardErrorId.TITLE
		);

	if (
		"date" in flashcardSet &&
		(typeof flashcardSet.date !== "string" ||
			!isValid(new Date(flashcardSet.date)))
	)
		throw new EnhancedError(
			"Date string is malformed. A valid date string is required",
			FlashcardErrorId.DATE
		);

	if (
		!("description" in flashcardSet) ||
		typeof flashcardSet.description !== "string"
	)
		throw new EnhancedError(
			"Description must be a defined string",
			FlashcardErrorId.DESCRIPTION
		);

	if (!("source" in flashcardSet) || !isFlashcardSetSource(flashcardSet.source))
		throw new EnhancedError("Invalid source", FlashcardErrorId.SOURCE);

	if (flashcardSet.source === "url") {
		if (!("url" in flashcardSet))
			throw new EnhancedError(
				"A URL is required when Source is 'URL'",
				FlashcardErrorId.URL
			);

		if (typeof flashcardSet.url !== "string" || !isUrl(flashcardSet.url))
			throw new EnhancedError("URL must be a valid URL", FlashcardErrorId.URL);
	}

	if (flashcardSet.source === "file" && !file)
		throw new EnhancedError(
			"A file is required when Source is 'File'",
			FlashcardErrorId.FILE
		);

	if (flashcardSet.source === "note") {
		if (!("noteId" in flashcardSet))
			throw new EnhancedError(
				"Note is required when Source is 'Note'",
				FlashcardErrorId.NOTE
			);

		if (typeof flashcardSet.noteId !== "string")
			throw new EnhancedError(
				"Note ID must be a string",
				FlashcardErrorId.NOTE
			);

		if (!isHexId(flashcardSet.noteId))
			throw new EnhancedError(
				"Note ID must be a valid ID",
				FlashcardErrorId.NOTE
			);
	}
}

function assertsValidFlashcard(
	flashcard: unknown,
	source: FlashcardSet["source"],
	index: number
): asserts flashcard is Flashcard {
	if (!flashcard || typeof flashcard !== "object")
		throw new EnhancedError(
			"Flashcard is malformed",
			`${FlashcardErrorId.CARDS}_${index}`
		);

	if (source !== "manual" && (!("id" in flashcard) || !isHexId(flashcard.id)))
		throw new EnhancedError(
			"Flashcard requires a valid unique ID",
			`${FlashcardErrorId.CARDS}_${index}`
		);

	if (
		!("definition" in flashcard) ||
		!flashcard.definition ||
		typeof flashcard.definition !== "object" ||
		!("text" in flashcard.definition) ||
		typeof flashcard.definition.text !== "string" ||
		!flashcard.definition.text.trim()
	)
		throw new EnhancedError(
			"Definition cannot be an empty string",
			`${FlashcardErrorId.CARDS}_${index}`
		);

	if (
		!("term" in flashcard) ||
		!flashcard.term ||
		typeof flashcard.term !== "string" ||
		!flashcard.term.trim()
	)
		throw new EnhancedError(
			"Term cannot be an empty string",
			`${FlashcardErrorId.CARDS}_${index}`
		);
}

export const isValidPartialFlashcardSet = (
	flashcardSet: unknown,
	file?: File
): flashcardSet is FlashcardSetInfoForSource => {
	try {
		assertsValidPartialFlashcardSet(flashcardSet, file);
		return true;
	} catch {
		return false;
	}
};

export function assertsValidFlashcardSet(
	flashcardSet: unknown,
	file?: File
): asserts flashcardSet is FlashcardSet {
	assertsValidPartialFlashcardSet(flashcardSet, file);

	if (!("cards" in flashcardSet && Array.isArray(flashcardSet.cards)))
		throw new EnhancedError("Flashcards are malformed", FlashcardErrorId.CARDS);

	flashcardSet.cards.forEach((c, i) =>
		assertsValidFlashcard(c, flashcardSet.source, i)
	);

	if (flashcardSet.source === "manual") return;

	if (!("id" in flashcardSet) || !isHexId(flashcardSet.id))
		throw new Error(
			"Generated flashcard set must have a valid ID in order to submit. Try again"
		);

	if (
		!("repetitions" in flashcardSet) ||
		!isEqual(flashcardSet.repetitions, [])
	)
		throw new Error("Flashcard set repetitions are malformed");

	if (!("isCompleted" in flashcardSet) || flashcardSet.isCompleted !== false)
		throw new Error(
			"Completion flag must be false when creating a new flashcard set"
		);

	if (
		!("meta" in flashcardSet) ||
		!flashcardSet.meta ||
		typeof flashcardSet.meta !== "object"
	)
		throw new Error(
			"Generated flashcard set contains malformed metadata. Try again"
		);

	if (
		!("createdAt" in flashcardSet.meta) ||
		!("updatedAt" in flashcardSet.meta) ||
		!("version" in flashcardSet.meta)
	)
		throw new Error(
			"Generated flashcard set contains malformed metadata. Try again"
		);

	if (
		typeof flashcardSet.meta.createdAt !== "string" ||
		typeof flashcardSet.meta.updatedAt !== "string" ||
		typeof flashcardSet.meta.version !== "number"
	)
		throw new Error(
			"Generated flashcard set contains malformed metadata. Try again"
		);
}

export const isValidFlashcardSet = (
	flashcardSet: unknown,
	file?: File
): flashcardSet is FlashcardSet => {
	try {
		assertsValidFlashcardSet(flashcardSet, file);

		return true;
	} catch {
		return false;
	}
};

/**
 * Calculate the priority of a flashcard based on the SuperMemo 2 algorithm
 * @param {Flashcard} flashcard
 * @returns
 */
const calculateFlashcardPriority = ({
	interval,
	efactor,
	grade = 0,
}: Flashcard): number => {
	// SuperMemo 2 algorithm calculation
	let priority = interval * efactor;
	if (grade >= 3) priority *= efactor;
	else priority /= 2; // Decrease priority for incorrect responses

	return priority;
};

export const sortBySpacedRepetitionPriority = (a: Flashcard, b: Flashcard) =>
	calculateFlashcardPriority(a) - calculateFlashcardPriority(b);

/**
 * Sort flashcard sets by completion status and date
 *
 * Flashcards that are incomplete come first by due date, followed by older flashcard sets
 * @param {FlashcardSet} a first flashcard set
 * @param {FlashcardSet} b second flashcard set
 * @returns {number} negative if a comes before b, positive if a comes after b, 0 if they are equal
 */
export const sortFlashcardSetsByCompletion = <T extends FlashcardSet>(
	a: T,
	b: T
) => {
	// Incomplete items come first
	if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;

	// Parse dates only if they are defined
	const dateA = a.date ? new Date(a.date).getTime() : NaN;
	const dateB = b.date ? new Date(b.date).getTime() : NaN;

	// If both dates are valid, sort by older dates first
	if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) return dateA - dateB;

	// If one date is undefined or invalid, place it last
	if (Number.isNaN(dateA)) return 1;
	if (Number.isNaN(dateB)) return -1;

	// If both dates are undefined, maintain relative order
	return 0;
};

export const decodeFlashcardSet = ({
	cards,
	title,
	...flashcardSet
}: FlashcardSetPopulated): FlashcardSetPopulated => ({
	...flashcardSet,
	title: decodeHtml(title),
	cards: cards.map(({ definition, term, ...c }) => ({
		...c,
		definition: {
			...definition,
			text: decodeHtml(definition.text),
		},
		term: decodeHtml(term),
	})),
});
